/* eslint-disable react-hooks/exhaustive-deps */
import Loading from "components/Loading";
import TruncatedText, { truncateText } from "components/TruncatedText";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  fetchWitnessProction,
  fetchWitnessProctionByAssignedProsecutor,
  fetchWitnessProctionForCoordinator,
  fetchWitnessProctionForRegistrar,
} from "services/witnessProtectionService";
import {
  Roles,
  WitnessProtection,
  WitnessProtectionDecision,
  WitnessProtectionStatus,
} from "types";
import { errorHandler, hasRole } from "utils/helper";
import PageHeader from "../../components/PageHeader";

const WP_STAGE_STYLES: Record<string, string> = {
  green: "bg-green-100 text-green-800 border-green-200",
  red: "bg-red-100 text-red-800 border-red-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  indigo: "bg-indigo-100 text-indigo-800 border-indigo-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  purple: "bg-purple-100 text-purple-800 border-purple-200",
  gray: "bg-gray-100 text-gray-800 border-gray-300",
};

// Lifecycle-aware badge (mirrors the detail page). The raw `status` returns to
// "Forwarded to Coordinator" both before and after the prosecutor's feedback,
// so we read protection_type / prosecutor_feedback / decision_status too.
const deriveWPStage = (
  p: any,
  t: any,
): { label: string; color: string; icon: string } => {
  const status = (p?.status || "").toString();
  const decision = (p?.decision_status || "").toString();

  if (decision === "Granted")
    return {
      label: t("witness_protection.stage.granted", {
        defaultValue: "Protection Granted",
      }),
      color: "green",
      icon: "✅",
    };
  if (decision === "Rejected")
    return {
      label: t("witness_protection.stage.rejected", {
        defaultValue: "Protection Rejected",
      }),
      color: "red",
      icon: "⛔",
    };

  switch (status) {
    case "Pending":
      return {
        label: t("witness_protection.stage.intake", {
          defaultValue: "Intake — Registering Witnesses",
        }),
        color: "amber",
        icon: "📝",
      };
    case "Forwarded to Coordinator":
      if (p?.prosecutor_feedback)
        return {
          label: t("witness_protection.stage.coordinator_review", {
            defaultValue: "Awaiting Coordinator Review",
          }),
          color: "amber",
          icon: "⏳",
        };
      if (!p?.protection_type)
        return {
          label: t("witness_protection.stage.with_coordinator", {
            defaultValue: "With Coordinator",
          }),
          color: "purple",
          icon: "📥",
        };
      return {
        label: t("witness_protection.stage.awaiting_assignment", {
          defaultValue: "Awaiting Prosecutor Assignment",
        }),
        color: "purple",
        icon: "👨‍⚖️",
      };
    case "Assigned to Prosecutor":
      return {
        label: t("witness_protection.stage.with_prosecutor", {
          defaultValue: "With Prosecutor",
        }),
        color: "indigo",
        icon: "👨‍⚖️",
      };
    case "Forwarded to Director":
      return {
        label: t("witness_protection.stage.with_director", {
          defaultValue: "With Director",
        }),
        color: "blue",
        icon: "📤",
      };
    case "Forwarded to Deputy":
      return {
        label: t("witness_protection.stage.awaiting_final_decision", {
          defaultValue: "Awaiting Final Decision",
        }),
        color: "amber",
        icon: "⚖️",
      };
    default:
      return {
        label:
          WitnessProtectionStatus[
            status as keyof typeof WitnessProtectionStatus
          ] ||
          status ||
          t("na"),
        color: "gray",
        icon: "",
      };
  }
};

const StatusBadge: React.FC<{ protection: any; t: any }> = ({
  protection,
  t,
}) => {
  const { label, color, icon } = deriveWPStage(protection, t);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium border ${
        WP_STAGE_STYLES[color] || WP_STAGE_STYLES.gray
      }`}
      title={truncateText(label) !== String(label ?? "") ? String(label) : undefined}
    >
      {icon && <span>{icon}</span>}
      {truncateText(label)}
    </span>
  );
};

const WitnessProtectionList: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("All");
  const [protections, setProtections] = useState<WitnessProtection[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isVariousCoordinator = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
  ]);
  const isEconomicCoordinator = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
  ]);
  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);
  const isChildWomen = hasRole(loginData?.roles, [Roles.childWomenCoordinator]);

  // Directors see the same scope as their crime-type coordinator, via the
  // by-admin-coordinator endpoint mapped to the matching coordinator role.
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isEconomicDirector = hasRole(loginData?.roles, [Roles.economicDirector]);
  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);

  const isZonalVariousChildRegistrar = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenRegistrar,
  ]);
  const isZonalEconomicCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionRegistrsrar,
  ]);

  const fetchData = () => {
    setLoading(true);
    const api = isProsecutor
      ? fetchWitnessProctionByAssignedProsecutor(loginData?.user_id || "")
      : isZonalVariousChildRegistrar || isZonalEconomicCorruptionRegistrar
        ? fetchWitnessProctionForCoordinator(
            loginData?.admin_unit + "" || "",
            isZonalVariousChildRegistrar
              ? Roles.zonalVariousChildWomenRegistrar
              : Roles.zonalEconomicCoruptionRegistrsrar,
          )
        : isVariousCoordinator
          ? fetchWitnessProctionForCoordinator(
              loginData?.admin_unit?.toString() || "",
              Roles.variousCoordinator,
            )
          : isEconomicCoordinator
            ? fetchWitnessProctionForCoordinator(
                loginData?.admin_unit?.toString() || "",
                Roles.economicCoordinator,
              )
            : isCorruptionCoordinator
              ? fetchWitnessProctionForCoordinator(
                  loginData?.admin_unit?.toString() || "",
                  Roles.corruptionCoordinator,
                )
              : isChildWomen
                ? fetchWitnessProctionForCoordinator(
                    loginData?.admin_unit?.toString() || "",
                    Roles.childWomenCoordinator,
                  )
                : isCorruptionDirector
                  ? fetchWitnessProctionForCoordinator(
                      loginData?.admin_unit?.toString() || "",
                      Roles.corruptionCoordinator,
                    )
                  : isEconomicDirector
                    ? fetchWitnessProctionForCoordinator(
                        loginData?.admin_unit?.toString() || "",
                        Roles.economicCoordinator,
                      )
                    : isVariousDirector
                      ? fetchWitnessProctionForCoordinator(
                          loginData?.admin_unit?.toString() || "",
                          Roles.variousCoordinator,
                        )
                      : isRegistrar
                        ? fetchWitnessProctionForRegistrar(
                            loginData?.admin_unit?.toString() || "",
                          )
                        : fetchWitnessProction();
    api
      .then((data) => {
        setProtections(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const searchTerm = search.trim().toLowerCase();
  const filtered = protections.filter((protection) => {
    const crime_type = protection?.crime_type?.toString()?.toLowerCase() || "";
    const applicant_full_name =
      protection?.applicant_full_name?.toString()?.toLowerCase() || "";
    const status = protection?.status?.toString()?.toLowerCase() || "";
    const court_registration_number =
      protection?.court_registration_number?.toString()?.toLowerCase() || "";

    const matchesText =
      crime_type.includes(searchTerm) ||
      applicant_full_name.includes(searchTerm) ||
      status.includes(searchTerm) ||
      court_registration_number.includes(searchTerm);

    return matchesText;
  });

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title={t("witness_protection.list.title")}
        icon={FaBriefcase}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        rightContent={
          <>
            {isRegistrar && (
              <button
                onClick={() => navigate("/witness-protection/request")}
                className="px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded hover:bg-red-700"
              >
                {t("witness_protection.list.register_button")}
              </button>
            )}
          </>
        }
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-x-auto bg-white shadow rounded-xl">
          {loading ? (
            <Loading
              label={`${t("loading")} ${t("witness_protection.list.title")}`}
            />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      {/* Left: Back + Title */}
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title={t("witness_protection.list.go_back")}
                        >
                          ◀️
                        </button>
                        <span>{t("witness_protection.list.table_title")}</span>
                      </div>

                      {/* Right: Search Box */}
                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t(
                            "witness_protection.list.search_placeholder",
                          )}
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr className="text-xs font-semibold uppercase tracking-wide text-gray-200">
                  <th className="p-3 text-left">
                    {t("witness_protection.list.applicant")}
                  </th>
                  <th className="p-3 text-left">
                    {t("witness_protection.list.court_reg_no")}
                  </th>
                  <th className="p-3 text-left">
                    {t("witness_protection.list.decision")}
                  </th>
                  <th className="p-3 text-left">
                    {t("witness_protection.list.assigned_prosecutor")}
                  </th>
                  <th className="p-3 text-left">
                    {t("witness_protection.list.status")}
                  </th>
                  <th className="p-3 text-right">
                    {t("witness_protection.list.detail")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((protection, idx) => (
                  <tr
                    key={protection?.id ?? idx}
                    className={`border-b border-gray-100 align-middle transition-colors hover:bg-gray-100 ${
                      idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"
                    }`}
                  >
                    <td className="p-3 font-[500] font-[Poppins] text-gray-900">
                      <TruncatedText value={protection?.applicant_full_name} />
                    </td>
                    <td className="p-3 font-[500] font-[Poppins] text-gray-700">
                      <TruncatedText value={protection?.court_registration_number} />
                    </td>
                    <td className="p-3 font-[500] font-[Poppins] text-gray-700">
                      <TruncatedText
                        value={
                          protection.decision_status
                            ? WitnessProtectionDecision[protection?.decision_status]
                            : ""
                        }
                      />
                    </td>
                    <td className="p-3 font-[500] font-[Poppins] text-gray-700">
                      <TruncatedText value={protection?.assign_prosecutor_name} />
                    </td>
                    <td className="p-3 font-[500] font-[inter]">
                      <StatusBadge protection={protection} t={t} />
                    </td>

                    <td className="p-3 font-[500] font-[Poppins] text-right">
                      <button
                        type="button"
                        onClick={() => {
                          navigate(
                            "/witness-protection/" + protection?.id + "/",
                          );
                        }}
                        className="whitespace-nowrap px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                      >
                        {t("witness_protection.list.view_button")}
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      {t("witness_protection.list.no_protections_found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default WitnessProtectionList;
